// CalendarEventChip — type-coded chip representing one Activity in a
// calendar grid cell. The chip carries three orthogonal signals:
//
//   • activity TYPE = a small leading colour dot (≤8px), positioned
//     after the contract ref. Visible on EVERY chip regardless of
//     status so a red-overdue chip still reads as "this is a KPI"
//     at a glance (spec §4). The dot uses KIND_DOT_BG from the
//     shared kindVocabulary so the three legend surfaces cannot drift.
//
//   • activity STATUS = the chip's surface fill + border. Neutral
//     statuses keep a light surface in light mode + a dark surface
//     in dark mode (was bg-slate-800 unconditionally which read as
//     dark navy chips against a light app theme — spec §5).
//
//   • SELECTED state composes a brand-tinted overlay on top of
//     whichever surface won.
//
// Severity NEVER conveys signal by colour alone (brand R2): the
// statusLabel text is always rendered.

import {
  KIND_DOT_BG,
  type ScheduleKind,
} from "./kindVocabulary";
import type { Activity } from "../../../types/schedule";

interface CalendarEventChipProps {
  activity: Activity;
  selected: boolean;
  onClick: () => void;
}

export default function CalendarEventChip({
  activity,
  selected,
  onClick,
}: CalendarEventChipProps) {
  const { kind, name, statusLabel, status, contract, series } = activity;

  // Severity owns the fill + border. Body text stays slate regardless
  // of severity so a red-overdue chip's body reads with the same
  // weight as a neutral chip — the red surface (border + tinted bg)
  // carries the severity signal, and the statusLabel text (always in
  // the DOM per brand R2) keeps the readout uncoloured. Amber slippage
  // gets its own visible surface so the legend's amber swatch doesn't
  // lie.
  let surface: string;
  if (status === "red") {
    surface =
      "bg-red-500/10 text-slate-800 border-red-500 dark:bg-red-500/15 dark:text-slate-100";
  } else if (status === "brand") {
    surface =
      "bg-brand-500/12 text-slate-800 border-brand-500 dark:bg-brand-500/15 dark:text-slate-100";
  } else if (status === "amber") {
    surface =
      "bg-amber-500/10 text-slate-800 border-amber-500 dark:bg-amber-500/15 dark:text-slate-100";
  } else {
    surface =
      "bg-white text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600";
  }

  const selectedSurface = selected
    ? "bg-brand-50 text-slate-900 ring-1 ring-brand-500 ring-inset dark:bg-brand-950 dark:text-slate-100"
    : "";

  // Aggregator only emits the five schedule kinds (variation removed);
  // narrowing the type here so the vocabulary lookup is total.
  const dotKind = kind as ScheduleKind;
  const dotClass = KIND_DOT_BG[dotKind] ?? "bg-slate-400";

  return (
    <button
      type="button"
      onClick={() => onClick()}
      aria-label={`${kind} · ${name} · ${contract.ref} · ${statusLabel}`}
      className={`flex w-full items-center gap-1.5 overflow-hidden rounded-sm border-l-[3px] py-[3px] pl-1 pr-1.5 text-left text-[11px] leading-tight transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 ${surface} ${selectedSurface}`}
    >
      <span className="shrink-0 font-mono text-[9.5px] text-slate-500 dark:text-slate-400">
        {contract.ref}
      </span>
      <span
        aria-hidden="true"
        className={`h-1.5 w-1.5 shrink-0 rounded-full ${dotClass}`}
      />
      <span className="truncate">{name}</span>
      {series && (
        <span
          className="shrink-0 text-[9.5px] text-slate-500 dark:text-slate-400"
          title={`Recurring · ${series.cadenceLabel} · occurrence ${series.index} of ${series.total}`}
          aria-label={`occurrence ${series.index} of ${series.total}`}
        >
          <span aria-hidden="true">↻</span>
          <span aria-hidden="true" className="ml-0.5">
            {series.index}
          </span>
        </span>
      )}
      {/* R2: severity is never colour-only — the label is always in
          the DOM so a non-colour reader still gets the severity. */}
      <span className="ml-auto shrink-0 text-[9.5px] text-slate-500 dark:text-slate-400">
        {statusLabel}
      </span>
    </button>
  );
}
